import { useCallback, useState } from "react";
import { updateItp } from "../../api";

const normalizeText = (value) => {
  return String(value || "")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .trim();
};

const normalizeLower = (value) => normalizeText(value).toLowerCase();

const COMMON_ITP_TYPES = [
  "Documents Review and Approval",
  "Assembly and Testing",
  "Preparation for Shipment",
];

const PRESSURE_ITP_TYPES = [
  "ASME Pressure Equipment Fabrication (Separate ITP will be provided if sub-contracted)",
  ...COMMON_ITP_TYPES,
].map(normalizeText);

const PIPING_ITP_TYPES = [
  "Piping Fabrication",
  ...COMMON_ITP_TYPES,
].map(normalizeText);

const getRowValue = (row, keys) => {
  if (!row || typeof row !== "object") return "";

  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null && row[key] !== "") {
      return row[key];
    }
  }

  return "";
};

const getItpRowId = (row) => {
  return getRowValue(row, ["id", "_id", "itpId", "ITP_ID"]);
};

const getItpRowType = (row) => {
  return normalizeText(
    getRowValue(row, [
      "type",
      "Type",
      "itpType",
      "ITP Type",
      "equipmentType",
      "equipment_type",
    ]),
  );
};

const getTaskCodeOnly = (taskCode) => {
  if (!taskCode) return "";
  return String(taskCode).split("-")[0].trim();
};

const getTaskCodeLevel = (taskCode) => {
  const codeOnly = getTaskCodeOnly(taskCode);
  if (!codeOnly) return 0;
  return codeOnly.split(".").filter(Boolean).length;
};

const getParentTaskCode = (taskCode) => {
  const codeOnly = getTaskCodeOnly(taskCode);
  if (!codeOnly) return "";

  const parts = codeOnly.split(".").filter(Boolean);
  if (parts.length < 2) return "";

  return `${parts[0]}.${parts[1]}`;
};

const parseTaskCode = (taskCode) => {
  if (!taskCode || typeof taskCode !== "string") {
    return { code: "", groupName: "" };
  }

  const s = String(taskCode).trim();
  const dashIdx = s.indexOf("-");

  if (dashIdx < 0) {
    return { code: s, groupName: "" };
  }

  return {
    code: s.slice(0, dashIdx).trim(),
    groupName: s.slice(dashIdx + 1).trim(),
  };
};

const getTaskValueForTagNumber = (task) => {
  return (
    task?.task ||
    task?.name ||
    parseTaskCode(task?.taskCode || "").groupName ||
    task?.taskCode ||
    ""
  );
};

const isItpTask = (task) => {
  return normalizeLower(task?.type) === "itp";
};

const isTwoLevelTask = (task) => {
  return getTaskCodeLevel(task?.taskCode) === 2;
};

const isThreeLevelTask = (task) => {
  return getTaskCodeLevel(task?.taskCode) === 3;
};

const getTaskText = (task) => {
  return normalizeLower(task?.task || task?.name || task?.taskCode || "");
};

const getDisciplineFromType = (type) => {
  const normalizedType = normalizeText(type);

  if (normalizedType === "Piping Fabrication") {
    return "piping";
  }

  if (
    normalizedType ===
    "ASME Pressure Equipment Fabrication (Separate ITP will be provided if sub-contracted)"
  ) {
    return "pressure";
  }

  return "";
};

const getDisciplineFromSelectedType = (selectedType) => {
  const directDiscipline = getDisciplineFromType(selectedType);
  if (directDiscipline) return directDiscipline;

  return "";
};

const getParentTaskByDiscipline = (allTasks, discipline) => {
  const tasks = Array.isArray(allTasks) ? allTasks : [];

  return tasks.find((task) => {
    if (!isItpTask(task)) return false;
    if (!isTwoLevelTask(task)) return false;

    const taskText = getTaskText(task);

    return taskText.includes(discipline);
  });
};

const getChildrenByParentTask = (allTasks, parentTask) => {
  const tasks = Array.isArray(allTasks) ? allTasks : [];
  const parentCode = getParentTaskCode(parentTask?.taskCode);

  if (!parentCode) return [];

  return tasks.filter((task) => {
    if (!isItpTask(task)) return false;
    if (!isThreeLevelTask(task)) return false;

    return getParentTaskCode(task?.taskCode) === parentCode;
  });
};

export const useSendToInspectionPlan = ({
  allTasks,
  itpRows,
  selectedType,
  setSnackbar,
  refreshItpRows,
}) => {
  const [isSendingToInspectionPlan, setIsSendingToInspectionPlan] =
    useState(false);

  const notify = useCallback(
    (message, severity = "info") => {
      if (typeof setSnackbar === "function") {
        setSnackbar({
          open: true,
          message,
          severity,
        });
        return;
      }

      if (severity === "error") {
        console.error(message);
      } else {
        console.log(message);
      }
    },
    [setSnackbar],
  );

  const sendToInspectionPlan = useCallback(
    async (selectedRows) => {
      const checkedRows = Array.isArray(selectedRows) ? selectedRows : [];

      if (checkedRows.length === 0) {
        notify("Please select at least one ITP row.", "warning");
        return;
      }

      const discipline = getDisciplineFromSelectedType(selectedType);

      if (!discipline) {
        notify(
          "Selected ITP type is common. Please select Piping Fabrication or Pressure Equipment Fabrication type to identify the target task discipline.",
          "warning",
        );
        return;
      }

      try {
        setIsSendingToInspectionPlan(true);

        const parentTask = getParentTaskByDiscipline(allTasks, discipline);
        console.log("tasksssss", allTasks);
        if (!parentTask) {
          notify(
            `No ${discipline} parent ITP task found in the schedule.`,
            "warning",
          );
          return;
        }

        const childTasks = getChildrenByParentTask(allTasks, parentTask);

        if (childTasks.length === 0) {
          notify(
            `No 3-level child ITP tasks found for ${discipline}.`,
            "warning",
          );
          return;
        }

        const visibleRows = Array.isArray(itpRows) ? itpRows : [];

        const updateRequests = checkedRows
          .map((row) => {
            const rowId = getItpRowId(row);
            if (!rowId) return null;

            const rowIndex = visibleRows.findIndex((item) => {
              return String(getItpRowId(item)) === String(rowId);
            });

            const childTask =
              childTasks[rowIndex >= 0 ? rowIndex : 0] || childTasks[0];

            const tagNumber = getTaskValueForTagNumber(childTask);

            if (!tagNumber) return null;

            return updateItp(rowId, {
              tagNumber,
            });
          })
          .filter(Boolean);

        if (updateRequests.length === 0) {
          notify("No matching rows found to send.", "warning");
          return;
        }

        await Promise.all(updateRequests);

        notify(
          "Selected rows sent to Inspection Plan successfully.",
          "success",
        );

        if (typeof refreshItpRows === "function") {
          await refreshItpRows();
        }
      } catch (error) {
        console.error("[Send To Inspection Plan] Failed", error);

        notify(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to send selected rows to Inspection Plan.",
          "error",
        );
      } finally {
        setIsSendingToInspectionPlan(false);
      }
    },
    [allTasks, itpRows, selectedType, notify, refreshItpRows],
  );

  return {
    isSendingToInspectionPlan,
    sendToInspectionPlan,
  };
};